import { FC, useState, useContext } from 'react';
import { Modal, Form, Input } from 'antd';
import { sendEmail } from '../../services/sendEmail';
import { useOrganizationParameters } from '../../use/useOrganizationParameters';
import { MessageContext } from '../../providers/MessageContext';

const { TextArea } = Input;

/**
 * Props for AttachmentEmailModal component
 */
interface AttachmentEmailModalProps {
    /** Whether the modal is visible */
    open: boolean;
    /** Function to call when modal is cancelled */
    onCancel: () => void;
    /** File name for the attachment */
    fileName: string;
    /** Base64 content of the file */
    fileContent?: string;
    /** Content type of the file */
    contentType?: string;
}

/**
 * Modal component for sending attachments via email
 */
const AttachmentEmailModal: FC<AttachmentEmailModalProps> = ({
    open,
    onCancel,
    fileName,
    fileContent,
    contentType = 'application/octet-stream'
}) => {
    const [form] = Form.useForm();
    const { messageApi } = useContext(MessageContext);
    const [loading, setLoading] = useState(false);
    const { loading: isOrgParamsLoading } = useOrganizationParameters();

    const handleSend = async () => {
        if (!fileContent) {
            messageApi.error('No file content available to send');
            return;
        }

        if (isOrgParamsLoading) {
            messageApi.warning('Organization details are still loading, please wait a moment and try again.');
            return;
        }
        
        setLoading(true);
        try {
            const values = await form.validateFields();
            
            await sendEmail({
                to: values.to.split(',').map((email: string) => email.trim()),
                subject: values.subject,
                text: values.message || `Please find the attached file: ${fileName}`,
                html: (values.message || `Please find the attached file: ${fileName}`).replace(/\n/g, '<br/>'),
                attachments: [{
                    filename: fileName,
                    content: fileContent,
                    contentType: contentType
                }]
            });

            messageApi.success('Email sent successfully');
            onCancel();
            form.resetFields();
        } catch (error) {
            if (error instanceof Error) {
                messageApi.error(`Failed to send email: ${error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
                title={`Send ${fileName} via Email`}
                open={open}
                onOk={handleSend}
                onCancel={() => {
                    onCancel();
                    form.resetFields();
                }}
                okText="Send"
                width={600}
                okButtonProps={{ 
                    loading: loading || isOrgParamsLoading,
                    disabled: !fileContent,
                    'aria-label': `Send email with ${fileName} attachment`
                }}
                cancelButtonProps={{
                    'aria-label': 'Cancel sending email'
                }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    requiredMark={false}
                    id="attachment-email-form"
                >
                    <Form.Item
                        name="to"
                        label="Recipients"
                        rules={[
                            { required: true, message: 'Please enter at least one email address' },
                            { 
                                pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+(,\s*[^\s@]+@[^\s@]+\.[^\s@]+)*$/,
                                message: 'Please enter valid email addresses separated by commas'
                            }
                        ]}
                    >
                        <Input 
                            placeholder="email@example.com, another@example.com" 
                            id="attachment-email-recipients"
                            aria-label="Email recipients"
                        />
                    </Form.Item>
                    <Form.Item
                        name="subject"
                        label="Subject"
                        initialValue={`File: ${fileName}`}
                        rules={[{ required: true, message: 'Please enter a subject' }]}
                    >
                        <Input 
                            id="attachment-email-subject"
                            aria-label="Email subject" 
                        />
                    </Form.Item>
                    <Form.Item
                        name="message"
                        label="Additional Message"
                    >
                        <TextArea 
                            rows={4} 
                            placeholder="Optional message to include with the file" 
                            id="attachment-email-message"
                            aria-label="Email message body"
                        />
                    </Form.Item>
                </Form>
            </Modal>
    );
};

export default AttachmentEmailModal; 